import { Alert, Box, Stack, Typography, useTheme } from '@mui/material';
import { useAuth } from '@/auth/AuthContext';
import { FieldRow } from '@/components/FieldRow';
import { StatusPill } from '@/components/StatusPill';
import { PageShell } from '@/layout/PageShell';
import { redactMiddle, utcStamp } from '@/utils/format';

/**
 * Read-only "what does the system know about me" page. Three sections:
 *
 *   - Identity         — claims pulled from the JWT
 *   - Tenant context   — tenant-id, current API base URL, whether the
 *                        request interceptor sees the same token we do
 *   - Session          — issued-at / expires-at, role
 *
 * The Security Policy summary lives on the Platform side (PR-B); a
 * tenant admin who wants to see "what's MY policy?" navigates there
 * if they have the role, or asks Platform. This page intentionally
 * doesn't surface security-policy details to plain tenant admins —
 * exposing rate limit + IP allowlist back through a tenant API would
 * leak operational posture.
 */

export function SettingsPage() {
  const theme = useTheme();
  const { claims, token } = useAuth();

  const issued = claims?.iat ? new Date(claims.iat * 1000).toISOString() : null;
  const expires = claims?.exp ? new Date(claims.exp * 1000).toISOString() : null;
  const apiBase =
    (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? '(same-origin)';

  return (
    <PageShell
      kicker="TENANT · SETTINGS"
      title="Settings"
      subtitle="Read-only view of your session, tenant context, and security posture. Mutations live in the Platform portal."
      width="wide"
    >
      <Stack spacing={3}>
        <Section title="Identity">
          <FieldRow label="Subject"  value={claims?.sub ?? '—'} mono />
          <FieldRow label="Role"     value={<StatusPill tone="info">{claims?.role ?? 'unknown'}</StatusPill>} />
          <FieldRow label="Tenant"   value={claims?.tenantId ? redactMiddle(claims.tenantId, 8, 8) : '—'} mono />
        </Section>

        <Section title="Tenant context">
          <FieldRow label="API base"        value={apiBase} mono />
          <FieldRow label="Header"          value={`X-Tenant-Id: ${claims?.tenantId ?? 'unset'}`} mono />
          <FieldRow
            label="Token state"
            value={
              token
                ? <StatusPill tone="success">Live (interceptor reads on every request)</StatusPill>
                : <StatusPill tone="danger">Missing</StatusPill>
            }
          />
        </Section>

        <Section title="Session">
          <FieldRow label="Issued at"  value={issued ? utcStamp(issued) : '—'} mono />
          <FieldRow label="Expires at" value={expires ? utcStamp(expires) : '—'} mono />
          <FieldRow
            label="Verified"
            value={
              claims?.verified
                ? <StatusPill tone="success">Yes</StatusPill>
                : <StatusPill tone="warning">Decode-only (dev mode)</StatusPill>
            }
          />
        </Section>

        <Alert severity="info" sx={{ borderRadius: theme.radius.sm + 'px' }}>
          Wiring updates (e.g. registering a webhook receiver, editing your security policy) go through a Platform Owner today.
          Self-service write surfaces are queued for a follow-up PR.
        </Alert>

        <Section title="Raw JWT claims">
          <Box
            component="pre"
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 11,
              bgcolor: theme.palette.surface.sunken,
              borderRadius: theme.radius.sm + 'px',
              p: 2,
              m: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {claims ? JSON.stringify(claims, null, 2) : '{}'}
          </Box>
        </Section>
      </Stack>
    </PageShell>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        bgcolor: theme.palette.surface.card,
        border: `1px solid ${theme.palette.border.subtle}`,
        borderRadius: theme.radius.md + 'px',
        p: 3,
      }}
    >
      <Typography variant="overline" color="text.tertiary" sx={{ mb: 1, display: 'block' }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}
